import type { components } from '@fluencelabs/vodopad-admin-api'

import {
  type CapacityRow,
  computeIpRows,
  computeRows,
  type IpRow,
} from './capacitySummary'

type Cluster = components['schemas']['ClusterCapacityDto']
type IpFamily = components['schemas']['IpAddressType']

/** Allocatable split into sold, internal, platform pods, other and free; other is what neither source explains. */
export function usageSegments(row: CapacityRow) {
  const known = row.sold + row.internal + (row.platformPods ?? 0) + row.free
  return [
    { label: 'Sold', value: row.sold },
    { label: 'Internal groups', value: row.internal },
    { label: 'Platform pods', value: row.platformPods ?? 0 },
    { label: 'Other', value: Math.max(row.allocatable - known, 0) },
    { label: 'Free', value: row.free },
  ]
}

export function ipUsageSegments(row: IpRow) {
  return [
    { label: 'Sold', value: Number(row.sold) },
    { label: 'Platform', value: Number(row.platform) },
    { label: 'Free', value: Number(row.free) },
  ]
}

const add = (a?: number, b?: number) =>
  a === undefined && b === undefined ? undefined : (a ?? 0) + (b ?? 0)

/** Sums of live measured clusters; unmeasured ones are named instead of adding sales without hardware. */
export function combineClusters(clusters: Cluster[]) {
  const live = clusters.filter((cluster) => !cluster.removedAt)
  const unmeasured = live.filter((cluster) => !cluster.inventory)
  const measured = live.filter((cluster) => cluster.inventory)
  const rows = new Map<string, CapacityRow>()
  for (const row of measured.flatMap(computeRows)) {
    const seen = rows.get(row.resource)
    rows.set(
      row.resource,
      seen
        ? {
            resource: row.resource,
            total: seen.total + row.total,
            allocatable: seen.allocatable + row.allocatable,
            sold: seen.sold + row.sold,
            internal: seen.internal + row.internal,
            platformPods: add(seen.platformPods, row.platformPods),
            free: seen.free + row.free,
            physicalFree: add(seen.physicalFree, row.physicalFree),
            note: seen.note ?? row.note,
          }
        : row,
    )
  }
  const ips = new Map<IpFamily, IpRow>()
  for (const row of measured.flatMap(computeIpRows)) {
    const seen = ips.get(row.family)
    ips.set(
      row.family,
      seen
        ? {
            family: row.family,
            total: seen.total + row.total,
            sold: seen.sold + row.sold,
            platform: seen.platform + row.platform,
            free: seen.free + row.free,
          }
        : row,
    )
  }
  return {
    rows: [...rows.values()],
    ips: [...ips.values()],
    unmeasured: unmeasured.map((cluster) => cluster.name),
    oldestMeasurement: measured
      .flatMap((cluster) =>
        cluster.inventory ? [cluster.inventory.hardwareCollectedAt] : [],
      )
      .sort((a, b) => Date.parse(a) - Date.parse(b))
      .at(0),
  }
}
